import type { GameMode, RunResultResponse } from '@blockbattle/shared';

export type SavedResult = RunResultResponse & { runId: string; mode: GameMode; duelCode?: string };
const key = (userId: string) => `bb:confirmed-result:${userId}`;

/** Display-only receipt. It is never submitted as a score or used to award resources. */
export function saveResultCheckpoint(userId: string, result: SavedResult): void {
  try {
    localStorage.setItem(key(userId), JSON.stringify({ at: Date.now(), result }));
  } catch {
    /* Keep the live confirmed result when storage is unavailable. */
  }
}

export function readResultCheckpoint(userId: string): SavedResult | null {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(key(userId)) ?? 'null');
    if (
      !saved ||
      typeof saved !== 'object' ||
      !('at' in saved) ||
      typeof saved.at !== 'number' ||
      saved.at > Date.now() ||
      Date.now() - saved.at > 86_400_000 ||
      !('result' in saved)
    )
      return null;
    const value = saved.result;
    if (!value || typeof value !== 'object') return null;
    const number = (name: string): number | null => {
      const n: unknown = Reflect.get(value, name);
      return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : null;
    };
    const score = number('score'),
      clearedLines = number('clearedLines'),
      maxCombo = number('maxCombo'),
      placedPieces = number('placedPieces'),
      percentile = number('percentile');
    if (
      !('id' in value) ||
      typeof value.id !== 'string' ||
      !('runId' in value) ||
      typeof value.runId !== 'string' ||
      !('mode' in value) ||
      (value.mode !== 'DAILY' && value.mode !== 'DUEL' && value.mode !== 'PRACTICE') ||
      score === null ||
      clearedLines === null ||
      maxCombo === null ||
      placedPieces === null ||
      percentile === null ||
      !('rank' in value) ||
      (value.rank !== null && number('rank') === null) ||
      !('isPersonalBest' in value) ||
      typeof value.isPersonalBest !== 'boolean'
    )
      return null;
    // Restore the stable receipt fields, not optional UI payloads from an older release.
    return {
      id: value.id,
      runId: value.runId,
      mode: value.mode,
      score,
      clearedLines,
      maxCombo,
      placedPieces,
      percentile,
      rank: value.rank === null ? null : number('rank'),
      isPersonalBest: value.isPersonalBest,
      ...('assisted' in value && typeof value.assisted === 'boolean'
        ? { assisted: value.assisted }
        : {}),
      ...('review' in value && typeof value.review === 'boolean' ? { review: value.review } : {}),
      ...('duelCode' in value && typeof value.duelCode === 'string'
        ? { duelCode: value.duelCode }
        : {}),
    };
  } catch {
    return null;
  }
}
